import { Link as RemixLink, useRouteLoaderData } from "@remix-run/react";
import { Button, Text, Link } from "@chakra-ui/react";

import { useUserSubscriptionValue } from "#app/hooks/index.js";
import {
  ActiveRequestHelpPopover,
  HelpPopover,
} from "#app/components/index.jsx";
import { ManageSubscriptionButton } from "#app/components/buttons/index.jsx";

import { getDisplayPrice, plans } from "#app/config/stripe.js";
import { CheckoutButton } from "../../buttons/CheckoutButton.jsx";
import { PricingCard } from "./PricingCard.jsx";
import { actionButtonProps } from "./shared.jsx";

export function ProPricingCard({
  name = "Priority",
  colorScheme = "brand",
  ...props
} = {}) {
  const { pricingCurrency } = useRouteLoaderData("root");
  const price = getDisplayPrice("pro", pricingCurrency);

  const badgeText = useUserSubscriptionValue({
    none: () => "",
    trial: () => "",
    active: () => "",
    active_pro: () => "your plan",
  });

  const subscribeButton = (
    <CheckoutButton plan="pro" colorScheme={colorScheme} {...actionButtonProps}>
      Subscribe to {name}
    </CheckoutButton>
  );

  const actionButton = useUserSubscriptionValue({
    none: () => (
      <Button isDisabled colorScheme={colorScheme} {...actionButtonProps}>
        Subscribe to {name}
      </Button>
    ),
    trial: () => subscribeButton,
    active_student: () => subscribeButton,
    active_pro: () => (
      <Button colorScheme={colorScheme} isDisabled {...actionButtonProps}>
        Subscribe to {name}
      </Button>
    ),
    active: () => (
      <ManageSubscriptionButton
        colorScheme={colorScheme}
        {...actionButtonProps}
      >
        Upgrade to {name}
      </ManageSubscriptionButton>
    ),
  });

  const actionButtonHelperText = useUserSubscriptionValue({
    none: () => (
      <>
        {"You must "}
        <Link as={RemixLink} to="/login">
          log in
        </Link>
        {" to subscribe"}
      </>
    ),
    trial: () => "Checkout with Stripe",
    active_student: () => "Checkout with Stripe",
    active: () => "Manage with Stripe",
    active_pro: () => (
      <>
        {"You're subscribed to this plan. "}
        <ManageSubscriptionButton variant="link">
          Manage
        </ManageSubscriptionButton>
      </>
    ),
  });

  return (
    <PricingCard
      colorScheme={colorScheme}
      badgeText={badgeText}
      name={name}
      price={price}
      pricingCurrency={pricingCurrency}
      duration="/ mo"
      description="Get notified first. Great for popular sites."
      iconProps={{ color: "brand.400" }}
      features={[
        <>
          <Text>
            <HelpPopover
              buttonText="Priority"
              body={
                <>
                  <Text>
                    Priority notifications are{" "}
                    <strong>sent to you first</strong>, giving you an edge if
                    the same notification goes out to multiple people
                  </Text>
                  <Text mt={4}>And yes, they are unlimited!</Text>
                </>
              }
            />{" "}
            notifications
          </Text>
        </>,
        <Text>Email, Text, WhatsApp, and Push</Text>,
        <Text>
          {plans.pro.maxActiveRequests} <ActiveRequestHelpPopover />
        </Text>,
        <>
          <Text>
            Checks{" "}
            <HelpPopover
              buttonText="twice as often"
              body={
                <>
                  <Text mb={4}>
                    This plan checks the reservation page{" "}
                    <strong>twice as often</strong> as all other plans.
                  </Text>
                  <Text fontSize="sm" fontStyle="italic" color="subtle">
                    We tune the exact rate to each park, so it varies.
                  </Text>
                </>
              }
            />
          </Text>
        </>,
      ]}
      actionButton={actionButton}
      actionButtonHelperText={actionButtonHelperText}
      {...props}
    />
  );
}
